import logo from '@/assets/logo.png';
import { useSoulMapDocumentDownload } from '@/hooks/useSoulMapDocumentDownload';
import { useSoulMapDocumentRetry } from '@/hooks/useSoulMapDocumentRetry';
import { useSoulMapDocuments } from '@/hooks/useSoulMapDocuments';
import PracticeService from '@/services/practice-service';
import { useUserStore } from '@/store/use-user-store';
import { useProfileDisplayName } from '@/utils/profile-variables';
import {
  describeSoulMapDocumentFile,
  formatSoulMapDocumentDate,
  getFullSoulMapDocument,
  getSoulMapDocumentPhase,
  type SoulMapDocumentPhase,
} from '@/utils/soulmap-document-view';
import axios from 'axios';
import { Download, FileText, LoaderCircle, RefreshCw } from 'lucide-react';
import { useState } from 'react';
import toast from 'react-hot-toast';
import './soul-map-document-card.css';

type WaitingPhase = Exclude<
  SoulMapDocumentPhase,
  'offered' | 'ready' | 'failed' | 'needs-review'
>;

const CONFIRM_FALLBACK_ERROR =
  'We could not start your Soul Map document. Please try again.';

/** The backend's refusal sentence on a 400, when it sent one. */
function getConfirmRefusalReason(error: unknown): string | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 400) {
    return null;
  }
  const reason = (error.response.data as { reason?: unknown } | undefined)
    ?.reason;
  return typeof reason === 'string' && reason.trim() ? reason : null;
}

const ARC_COLOR = '#ee8571';

/** One dot per arc of the Soul Map, along the cover's single curve. */
const ARC_POINTS = [
  [72, 58],
  [174.5, 28.3],
  [285.5, 28.3],
  [388, 58],
] as const;

/** Each node pops in 110 ms after the previous one, as the arc draws. */
const NODE_DELAYS_MS = [340, 450, 560, 670] as const;

const PHASE_LABELS: Record<WaitingPhase, string> = {
  recomputing: 'Updating birth profile…',
  queued: 'Report queued…',
  writing: 'Creating your Soul Map…',
  rendering: 'Preparing PDF…',
};

const CARD_FRAME =
  'mb-2.5 mt-3.5 flex w-full overflow-hidden rounded-[10px] bg-white font-[family-name:Inter,ui-sans-serif,system-ui,sans-serif] md:mt-4 md:w-[460px] md:max-w-full';
const COVER_PANEL =
  'flex w-[100px] flex-shrink-0 items-center justify-center bg-[#f7efe9] py-3.5 md:w-[150px] md:py-5';
const CARD_TITLE =
  'font-[family-name:Fraunces,Georgia,serif] text-[15px] font-medium leading-[1.2] text-[#17161c] md:text-[19px]';
const CARD_META = 'text-[10.5px] text-[#68656f] md:text-xs';
const PRIMARY_BUTTON =
  'mt-[7px] flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-full border-0 bg-[linear-gradient(135deg,#8c9cee,#7383de)] px-2.5 py-3 text-[12.5px] font-semibold text-white shadow-[0_6px_16px_rgba(115,131,222,0.28)] transition-[transform,box-shadow] duration-[250ms] ease-out hover:-translate-y-px hover:shadow-[0_10px_22px_rgba(115,131,222,0.38)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#7383de] disabled:cursor-wait disabled:opacity-80 motion-reduce:transition-none motion-reduce:hover:translate-y-0 md:mt-2 md:w-auto md:gap-[7px] md:self-start md:px-5 md:text-[13px]';

function ArcMark({ compact }: { compact: boolean }) {
  return (
    <svg
      viewBox="62 8 336 62"
      className="block h-auto w-full"
      aria-hidden="true"
      focusable="false"
    >
      <path
        className="sm-doc-arc"
        d="M 72 58 C 72 16, 388 16, 388 58"
        pathLength={340}
        fill="none"
        stroke={ARC_COLOR}
        strokeWidth={compact ? 3.8 : 3}
        strokeLinecap="round"
        opacity={0.5}
      />
      {ARC_POINTS.map(([cx, cy], index) => (
        <circle
          key={cx}
          className="sm-doc-node"
          cx={cx}
          cy={cy}
          r={compact ? 10 : 8}
          fill={ARC_COLOR}
          style={{ animationDelay: `${NODE_DELAYS_MS[index]}ms` }}
        />
      ))}
    </svg>
  );
}

/** Miniature cover page: logo, title and arc mark. Decorative only. */
function CoverThumbnail() {
  return (
    <div className={COVER_PANEL} aria-hidden="true">
      <div className="flex aspect-[1/1.414] w-[74px] flex-col rounded-[2px] border border-[#e8e1d9] bg-white px-[7px] py-2 shadow-[0_4px_11px_rgba(23,22,28,0.16)] md:w-[116px] md:px-2.5 md:py-[11px] md:shadow-[0_5px_14px_rgba(23,22,28,0.16)]">
        <img
          src={logo}
          alt=""
          className="h-[7px] w-[7px] md:h-[9px] md:w-[9px]"
        />
        <div className="mt-auto">
          <span className="block font-[family-name:Fraunces,Georgia,serif] text-[9px] font-medium leading-[1.1] text-[#17161c] md:text-[13px]">
            Soul Map
          </span>
        </div>
        <div className="mt-auto pt-1.5 md:pt-[9px]">
          <span className="block md:hidden">
            <ArcMark compact />
          </span>
          <span className="hidden md:block">
            <ArcMark compact={false} />
          </span>
        </div>
      </div>
    </div>
  );
}

/** Skeleton shown while the document is queued, written or rendered. */
function WaitingCard({ phase }: { phase: WaitingPhase }) {
  return (
    <div className={CARD_FRAME} data-generation-guard-exempt>
      <div className={COVER_PANEL} aria-hidden="true">
        <div className="sm-doc-shimmer sm-doc-shimmer--page aspect-[1/1.414] w-[74px] rounded-[2px] border border-[#ece4dc] md:w-[116px]" />
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center gap-[5px] p-[13px] leading-[normal] md:gap-[9px] md:px-5 md:py-[18px]">
        <span className={CARD_TITLE}>Your Soul Map</span>
        <span className={CARD_META}>{PHASE_LABELS[phase]}</span>
        <div
          aria-hidden="true"
          className="sm-doc-shimmer sm-doc-shimmer--line my-0.5 h-[9px] w-[96px] rounded-[4px] md:my-0 md:w-[118px]"
        />
        <div
          aria-hidden="true"
          className="sm-doc-shimmer sm-doc-shimmer--button mt-[7px] h-11 w-full rounded-full md:mt-2 md:w-[132px]"
        />
      </div>
    </div>
  );
}

/**
 * An ordinary generation failure. The retry action shows only while the
 * backend allows it: a birth-profile failure is fixed through the
 * birth-details modal instead, and its retry would be refused.
 */
function FailedCard({
  onRetry,
  isRetrying = false,
}: {
  onRetry?: () => void;
  isRetrying?: boolean;
}) {
  return (
    <div className={CARD_FRAME} data-generation-guard-exempt>
      <CoverThumbnail />

      <div className="flex min-w-0 flex-1 flex-col justify-center gap-[5px] p-[13px] leading-[normal] md:gap-[9px] md:px-5 md:py-[18px]">
        <span className={CARD_TITLE}>Your Soul Map</span>
        <span className={CARD_META}>We couldn’t finish creating it.</span>
        <span className="text-[10.5px] text-[#68656f] md:text-xs">
          {onRetry
            ? 'Your conversation is safe. You can try again now.'
            : 'Your conversation is safe. Please try again later.'}
        </span>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            disabled={isRetrying}
            aria-busy={isRetrying}
            className={PRIMARY_BUTTON}
          >
            {isRetrying ? (
              <LoaderCircle
                className="h-3.5 w-3.5 animate-spin md:h-[15px] md:w-[15px]"
                aria-hidden="true"
              />
            ) : (
              <RefreshCw
                className="h-3.5 w-3.5 md:h-[15px] md:w-[15px]"
                aria-hidden="true"
              />
            )}
            {isRetrying ? 'Retrying…' : 'Try again'}
          </button>
        )}
      </div>
    </div>
  );
}

function NeedsReviewCard({
  errorMessage,
  onReview,
}: {
  errorMessage?: string;
  onReview?: () => void;
}) {
  return (
    <div className={CARD_FRAME} data-generation-guard-exempt>
      <CoverThumbnail />

      <div className="flex min-w-0 flex-1 flex-col justify-center gap-[5px] p-[13px] leading-[normal] md:gap-1.5 md:px-5 md:py-[18px]">
        <span className={CARD_TITLE}>Your Soul Map</span>
        <span className={CARD_META}>Needs confirmation</span>
        <span className="text-[10.5px] text-[#68656f] md:text-xs">
          {errorMessage ||
            'Review your birth details so we can draw this report again.'}
        </span>
        {onReview && (
          <button type="button" onClick={onReview} className={PRIMARY_BUTTON}>
            Review and regenerate report
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * The offer, under the agent's reply that made it: the deterministic
 * confirmation route from LE-296 F1 (option B). Clicking posts to the confirm
 * endpoint, which runs the permission checks and queues the job; the card then
 * moves to the queued phase on the next status read. The expired variant
 * offers a fresh copy in wording that never mentions birth details (LE-382).
 */
function OfferedCard({
  practiceId,
  onConfirmed,
  expired = false,
}: {
  practiceId: string;
  onConfirmed: () => Promise<unknown>;
  expired?: boolean;
}) {
  const [isConfirming, setIsConfirming] = useState(false);

  const handleConfirm = async () => {
    if (isConfirming) {
      return;
    }
    setIsConfirming(true);
    try {
      await PracticeService.confirmSoulMapDocument(Number(practiceId));
      await onConfirmed();
    } catch (error) {
      console.error('Soul Map document confirmation failed:', error);
      toast.error(getConfirmRefusalReason(error) ?? CONFIRM_FALLBACK_ERROR);
    } finally {
      setIsConfirming(false);
    }
  };

  return (
    <div className={CARD_FRAME} data-generation-guard-exempt>
      <CoverThumbnail />

      <div className="flex min-w-0 flex-1 flex-col justify-center gap-[5px] p-[13px] leading-[normal] md:gap-1.5 md:px-5 md:py-[18px]">
        <span className={CARD_TITLE}>Your Soul Map</span>
        <span className={CARD_META}>
          {expired
            ? 'Your Soul Map has expired. Create a fresh copy when you like.'
            : 'Ready to be written for you.'}
        </span>

        <button
          type="button"
          onClick={() => void handleConfirm()}
          disabled={isConfirming}
          aria-busy={isConfirming}
          className={PRIMARY_BUTTON}
        >
          {isConfirming ? (
            <LoaderCircle
              className="h-3.5 w-3.5 animate-spin md:h-[15px] md:w-[15px]"
              aria-hidden="true"
            />
          ) : (
            <FileText
              className="h-3.5 w-3.5 md:h-[15px] md:w-[15px]"
              aria-hidden="true"
            />
          )}
          {expired ? 'Create my Soul Map again' : 'Create my Soul Map document'}
        </button>
      </div>
    </div>
  );
}

interface SoulMapDocumentCardProps {
  practiceId: string;
  statusQuery: ReturnType<typeof useSoulMapDocuments>;
  treatExpiredAsPreparing?: boolean;
  /** Offer an expired document again instead of a birth-details review. */
  regenerateExpired?: boolean;
  onReviewBirthDetails?: () => void;
  /** Plays the reveal animation. Set only when the document has just turned ready. */
  animateReveal?: boolean;
}

/**
 * In-chat document card from the LiFT chat page design. Rendered inside the
 * assistant reply that led to the document, from report state on every load:
 * a skeleton with the current phase while the job runs, then the ready card.
 */
export function SoulMapDocumentCard({
  practiceId,
  statusQuery,
  treatExpiredAsPreparing = false,
  regenerateExpired = false,
  onReviewBirthDetails,
  animateReveal = false,
}: SoulMapDocumentCardProps) {
  const { data, refetch } = statusQuery;
  const phase = getSoulMapDocumentPhase(data, {
    treatExpiredAsPreparing,
    regenerateExpired,
  });
  const isExpiredOffer = phase === 'offered' && data?.status === 'expired';
  const pdf = getFullSoulMapDocument(data);
  const { download, isDownloading } = useSoulMapDocumentDownload(
    practiceId,
    statusQuery
  );
  const { retry, canRetry, isRetrying } = useSoulMapDocumentRetry(
    practiceId,
    statusQuery
  );
  const user = useUserStore((state) => state.user);
  const { fullName, isReady } = useProfileDisplayName();

  if (phase === null) {
    return null;
  }

  const announcement =
    phase === 'offered'
      ? isExpiredOffer
        ? 'Your Soul Map has expired. Create a fresh copy when you like.'
        : 'Your Soul Map is ready to be written. Create it when you like.'
      : phase === 'ready'
        ? 'Your Soul Map is ready to download.'
        : phase === 'failed'
          ? canRetry
            ? 'We could not create your Soul Map. You can try again now.'
            : 'We could not create your Soul Map. Please try again later.'
          : phase === 'needs-review'
            ? 'Your Soul Map needs confirmation before we can draw it again.'
            : `Your Soul Map: ${PHASE_LABELS[phase]}.`;

  // One live region that stays mounted across phases, so each change is read out.
  const liveRegion = (
    <span className="sr-only" role="status" aria-live="polite">
      {announcement}
    </span>
  );

  if (phase === 'offered') {
    return (
      <>
        {liveRegion}
        <OfferedCard
          practiceId={practiceId}
          onConfirmed={refetch}
          expired={isExpiredOffer}
        />
      </>
    );
  }

  if (phase === 'needs-review') {
    const recomputeError =
      typeof data?.birth_profile_recompute_error === 'string'
        ? data.birth_profile_recompute_error
        : data?.error;

    return (
      <>
        {liveRegion}
        <NeedsReviewCard
          errorMessage={recomputeError}
          onReview={onReviewBirthDetails}
        />
      </>
    );
  }

  if (phase === 'failed') {
    return (
      <>
        {liveRegion}
        <FailedCard
          onRetry={canRetry ? retry : undefined}
          isRetrying={isRetrying}
        />
      </>
    );
  }

  if (phase !== 'ready') {
    return (
      <>
        {liveRegion}
        <WaitingCard phase={phase} />
      </>
    );
  }

  if (!pdf) {
    return liveRegion;
  }

  const ownerName =
    isReady && fullName
      ? fullName
      : [user?.first_name, user?.last_name].filter(Boolean).join(' ');
  const documentDate = data?.generated_at ?? data?.completed_at;
  const longDate = formatSoulMapDocumentDate(documentDate, 'long');
  const shortDate = formatSoulMapDocumentDate(documentDate, 'short');
  const hasOwnerLine = Boolean(ownerName || longDate);

  return (
    <>
      {liveRegion}
      <div
        className={`${CARD_FRAME}${animateReveal ? ' sm-doc-reveal' : ''}`}
        data-generation-guard-exempt
      >
        <CoverThumbnail />

        <div className="flex min-w-0 flex-1 flex-col justify-center gap-[5px] p-[13px] leading-[normal] md:gap-1.5 md:px-5 md:py-[18px]">
          <span className={CARD_TITLE}>Your Soul Map</span>

          {hasOwnerLine && (
            <span className={CARD_META}>
              {ownerName}
              {ownerName && longDate ? ' · ' : ''}
              {shortDate && <span className="md:hidden">{shortDate}</span>}
              {longDate && <span className="hidden md:inline">{longDate}</span>}
            </span>
          )}

          <span className="text-[10.5px] text-[#68656f] md:text-[11.5px]">
            {describeSoulMapDocumentFile(pdf)}
          </span>

          <button
            type="button"
            onClick={() => void download(pdf.url)}
            disabled={isDownloading}
            aria-busy={isDownloading}
            aria-label="Download your Soul Map (PDF)"
            className={PRIMARY_BUTTON}
          >
            {isDownloading ? (
              <LoaderCircle
                className="h-3.5 w-3.5 animate-spin md:h-[15px] md:w-[15px]"
                aria-hidden="true"
              />
            ) : (
              <Download
                className="h-3.5 w-3.5 md:h-[15px] md:w-[15px]"
                aria-hidden="true"
              />
            )}
            Download
          </button>
        </div>
      </div>
    </>
  );
}
